// `import type` (not a value import): Node's native type-stripping runs the unit tests
// directly, and a value import would be resolved at runtime against a path that only
// carries types. Erasing it keeps `npm test -w @cineamo/api` working.
import type { AgeRatingValue, TrailerAgeRating } from '../models/movie-trailer/TrailerPlayback.types';

/**
 * Whether a trailer may play in an embedded player, under § 5 Abs. 4 JMStV (CIN-14724).
 *
 * ## The rule
 *
 * § 5 Abs. 1 JMStV obliges the Anbieter to ensure minors of the affected age group do not usually
 * perceive developmentally impairing content. § 5 Abs. 3 offers three equivalent ways to discharge
 * that — technical means, an age label a Jugendschutzprogramm can read, or a time limit. This is the
 * third, per § 5 Abs. 4:
 *
 * | Rating       | Window      | Basis                                                       |
 * | ------------ | ----------- | ----------------------------------------------------------- |
 * | 0, 6, 12     | none        | § 5 Abs. 5 — harmful only to under-14s, separation suffices  |
 * | 16           | 22:00–06:00 | § 5 Abs. 4 Satz 2                                            |
 * | 18 / unrated | 23:00–06:00 | § 5 Abs. 4 Satz 1                                            |
 *
 * Unrated is treated as 18: § 5 Abs. 2 presumes developmental impairment for anything not released
 * for the age group, so "we do not know" carries the strictest window, not none.
 *
 * Not covered here: § 5 Abs. 5 discharges 6 and 12 by keeping them "getrennt von Angeboten für
 * Kinder", not by a clock. That is a placement rule for children's surfaces, not something a time
 * check can express — so 12 returns playable, and the obligation to keep 12-rated trailers out of
 * children's contexts sits with whoever builds such a surface. We have none today.
 *
 * ## Scope: Germany, by country and not by zone
 *
 * The JMStV is German law, and Austria and Switzerland run separate regimes — different ladders
 * (`10` and `14` are Austrian steps that no § 5 Abs. 4 hour attaches to), different instruments. So
 * the gate keys on {@link GATED_COUNTRY} and every other country is returned ungated rather than
 * being handed German hours it was never subject to.
 *
 * An earlier revision keyed on an IANA zone instead. That reads as country-awareness and is not:
 * DE, AT and CH all run CET/CEST, so the zone is identical for exactly the three countries that
 * needed telling apart, and an Austrian `14` came out blocked all day under German rules.
 *
 * Adding AT or CH later means a second entry in {@link WINDOWS_BY_COUNTRY} and nothing else.
 *
 * ## The clock comes from the server
 *
 * `clock` is the wall-clock time the SERVER reads, fetched from `/api/legal-clock` — never
 * `new Date()` on the device. A viewer who moves their device clock forward would otherwise unlock
 * every trailer, which is exactly what § 5 Abs. 1's "üblicherweise nicht wahrnehmen" asks us to
 * prevent and the first thing a KJM review would try.
 *
 * It is a plain `"HH:MM"` string rather than an instant, because that is the whole of what the rule
 * needs: § 5 Abs. 4 names hours on a German wall clock, not moments on a timeline. Passing the time
 * already reduced to that form keeps every zone conversion on one side of the wire (the route
 * handler) instead of at each of the call sites.
 *
 * `null` means the clock is not known yet or could not be fetched, and a time-gated trailer stays
 * blocked: nothing has shown the window to be open, and falling back to the device would hand the
 * decision straight back to the viewer.
 */

/** The only country whose trailers this gate restricts. */
export const GATED_COUNTRY = 'DE';

/** The hour each rating's window opens; every window closes at {@link WINDOW_CLOSES_AT}. */
const WINDOW_OPENS_AT_16 = 22;
const WINDOW_OPENS_AT_18 = 23;
const WINDOW_CLOSES_AT = 6;

/**
 * Playback must finish inside the window, not merely start inside it (JuSchRiL 3.2.3), so the window
 * is treated as closing this many minutes early. A fixed allowance rather than the real length,
 * because no trailer source ships a runtime; 10 minutes clears the longest trailer we carry and only
 * ever moves the 06:00 edge, where traffic is effectively zero.
 */
const PLAYBACK_ALLOWANCE_MINUTES = 10;

/** A wall-clock time of day, `"HH:MM"` on a 24-hour clock — what `/api/legal-clock` returns. */
export type LegalClock = string;

export type TrailerPlaybackDecision = {
    /**
     * The rating the decision was taken on: the trailer's own where it has one, otherwise the film's.
     * Null when neither is known, which is treated as 18.
     */
    effectiveAgeRating?: AgeRatingValue | null;
    /**
     * True when `effectiveAgeRating` is the trailer's OWN Freigabe, and therefore the only case in
     * which it may be displayed as the trailer's rating. False means the film's was borrowed.
     */
    isOwnAgeRating: boolean;
    /**
     * Whether the trailer may be played in an embedded player.
     *
     * `false` does not mean "hide it": link out to YouTube instead, where Google is the Anbieter and
     * runs its own age gate. Blocking the embed is the obligation; removing the trailer is not.
     */
    embeddable: boolean;
    /**
     * The wall-clock time the window next opens, `"22:00"` or `"23:00"`, for "ab 22:00 Uhr" copy.
     * Absent when the trailer is embeddable, not time-gated, or the clock could not be read — in the
     * last case there is no opening we can honestly name.
     */
    embeddableFrom?: LegalClock;
};

/** The rungs of the FSK ladder — the only values the JMStV windows are defined for. */
const GERMAN_STEPS = [0, 6, 12, 16, 18];

/**
 * The German step a certification names, or null when it does not name one.
 *
 * Deliberately strict. § 5 Abs. 4 JMStV is German law and its hours attach to the FSK ladder, so only
 * a value that IS a rung counts — `'16'` and `'FSK 16'` do; `'12A'`, `'R'`, `'PG'`, `'TP'` and `'15'`
 * do not. Matching a loose `/\d+/` would read the UK's `12A` as FSK 12, which is a different rating
 * with different rules, and would state a German Freigabe the film never received.
 *
 * A non-German certification therefore returns null and is treated as unrated — the strictest window.
 * That is the safe direction: the alternative is inventing a rung the issuing body never assigned.
 */
function toGermanStep(ageRating: AgeRatingValue | null | undefined): number | null {
    if (ageRating == null) return null;

    const match = /^(?:FSK\s*)?(\d{1,2})$/i.exec(ageRating.trim());
    if (!match) return null;

    const step = Number(match[1]);

    return GERMAN_STEPS.includes(step) ? step : null;
}

/**
 * The hour a rating's window opens in a given country, or null when nothing gates it there.
 *
 * One entry per gated country: a country absent from here has no time gate at all, which is the
 * current state of AT and CH — not because they have no youth-protection regime, but because theirs
 * is not this one and guessing at it would be worse than leaving it to the operator.
 */
const WINDOWS_BY_COUNTRY: Record<string, (ageRating: AgeRatingValue | null | undefined) => number | null> = {
    [GATED_COUNTRY]: (ageRating) => {
        const step = toGermanStep(ageRating);

        // Unrated, or a rating from a body whose ladder the JMStV does not speak — strictest window.
        if (step === null || step >= 18) return WINDOW_OPENS_AT_18;
        if (step >= 16) return WINDOW_OPENS_AT_16;
        return null;
    }
};

/** Minutes since midnight that `"22:15"` names, or null when the string is not a clock reading. */
function toMinutes(clock: LegalClock): number | null {
    const match = /^(\d{2}):(\d{2})$/.exec(clock);
    if (!match) return null;

    const hour = Number(match[1]);
    const minute = Number(match[2]);

    return hour < 24 && minute < 60 ? hour * 60 + minute : null;
}

/** `"22:15"` — the reading of a wall clock in `timeZone` at `instant`. Used by `/api/legal-clock`. */
export function legalClockOf(instant: Date, timeZone: string): LegalClock {
    // `hourCycle: 'h23'` rather than `hour12: false`: the latter renders midnight as `24` in several
    // engines, which would put the first hour of the day past every window boundary.
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        hourCycle: 'h23',
        hour: '2-digit',
        minute: '2-digit'
    }).formatToParts(instant);
    const read = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '00';

    return `${read('hour')}:${read('minute')}`;
}

/**
 * @param clock The SERVER's wall-clock reading, `"HH:MM"`, from `useLegalClock()` — never the
 *     device's. `null` while it is still being fetched or after the fetch failed, which blocks a
 *     time-gated trailer rather than falling back to the viewer's own clock.
 * @param countryCode The country whose regime applies — the cinema's on a single-cinema site, the
 *     URL locale's otherwise. Only {@link GATED_COUNTRY} is gated; anything else is ungated.
 */
export function resolveTrailerPlayback(
    rating: TrailerAgeRating | undefined,
    clock: LegalClock | null,
    countryCode: string | null | undefined
): TrailerPlaybackDecision {
    // No rating information at all — the response predates these fields. Behave as this surface did
    // before the gate existed rather than going dark on every trailer.
    if (!rating) {
        return { embeddable: true, isOwnAgeRating: false };
    }

    // The trailer's own Freigabe wins; the film's stands in when it has none. A trailer is virtually
    // never rated above its film, so borrowing the film's value over-restricts rather than under-.
    const isOwnAgeRating = rating.trailer != null;
    const effectiveAgeRating = rating.trailer ?? rating.film;
    const ungated = { embeddable: true, effectiveAgeRating, isOwnAgeRating };

    // Outside a gated country there is no window to be inside of, so the clock is never even read —
    // an Austrian or Swiss surface makes no request and shows no notice.
    const windowOf = WINDOWS_BY_COUNTRY[countryCode ?? ''];
    if (!windowOf) {
        return ungated;
    }

    const opensAt = windowOf(effectiveAgeRating);
    if (opensAt === null) {
        return ungated;
    }

    // Evaluated on the server's clock, never the device's — see the `clock` parameter.
    const nowMinutes = clock === null ? null : toMinutes(clock);

    // The clock has not arrived, or arrived malformed. We cannot say whether the window is open, so
    // the rating's restriction stands and no opening time is named: this is a time-gated trailer and
    // nothing has shown the window to be open.
    if (nowMinutes === null) {
        return { embeddable: false, effectiveAgeRating, isOwnAgeRating };
    }

    const opensAtMinutes = opensAt * 60;
    const closesAtMinutes = WINDOW_CLOSES_AT * 60 - PLAYBACK_ALLOWANCE_MINUTES;

    // Windows run across midnight (22:00 → 06:00), so this is two disjoint ranges on the clock, not
    // one: at or after the opening hour, or before the (allowance-adjusted) closing edge next morning.
    if (nowMinutes >= opensAtMinutes || nowMinutes < closesAtMinutes) {
        return ungated;
    }

    return {
        embeddable: false,
        embeddableFrom: `${String(opensAt).padStart(2, '0')}:00`,
        effectiveAgeRating,
        isOwnAgeRating
    };
}
